import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useChat } from "../hooks/useChat";
import { MessageCircle, Sparkles, HelpCircle, PenLine, BookOpen, Search, CheckCircle2 } from "lucide-react";
import { message } from "antd";
import FloatingChatMessage from "./FloatingChatMessage";
import FloatingChatInput from "./FloatingChatInput";
import { getRecipeList } from "../api/chatService";

// Modules whose chats benefit from a "pick a recipe" starting point instead
// of a blank input — keyed by ChatModule.key from the backend.
const RECIPE_PICKER_MODULE_KEYS = ["recipe-summarize", "recipe-qa"];

// Open-ended starting points that aren't tied to any specific backend module —
// they all land in the same general assistant, just with the composer seeded
// so the welcome screen doesn't read as "pick which bot handles this".
const GENERIC_SUGGESTIONS = [
  { key: "ask", label: "Ask a question", icon: HelpCircle, prefill: "" },
  { key: "write", label: "Write or draft something", icon: PenLine, prefill: "Help me write " },
  { key: "explain", label: "Explain a process", icon: BookOpen, prefill: "Explain how " },
  { key: "lookup", label: "Look something up", icon: Search, prefill: "" },
];

// Suggestion bubbles — same row used on the pre-chat welcome screen and,
// while there's no real conversation yet, inline above the composer too —
// so it never reads as a one-shot fork in the road.
const SuggestionBubbles = ({ modules, modulesLoading, activeModuleName, onGenericClick, onModuleClick }) => {
  if (modulesLoading) {
    return (
      <div className="flex justify-center items-center py-4">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-skin-button-base"></div>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap justify-center gap-2 max-h-48 overflow-y-auto scrollbar-thin px-1 py-1">
      {GENERIC_SUGGESTIONS.map((s) => {
        const Icon = s.icon;
        return (
          <motion.button
            key={s.key}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => onGenericClick(s)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-xs font-medium text-gray-700 dark:text-gray-200 transition-all duration-200"
          >
            <Icon size={13} className="text-skin-button-base" />
            {s.label}
          </motion.button>
        );
      })}
      {modules.map((mod) => {
        const isActive = mod.name === activeModuleName;
        return (
          <motion.button
            key={mod.id}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => onModuleClick(mod.id, mod.name)}
            title={mod.desc}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all duration-200 ${
              isActive
                ? "border-primary-500 bg-primary-500 text-white shadow-sm"
                : "border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 text-primary-700 dark:text-primary-200"
            }`}
          >
            {isActive ? <CheckCircle2 size={13} /> : <Sparkles size={13} />}
            {mod.name}
          </motion.button>
        );
      })}
    </div>
  );
};

const FloatingChatContent = () => {
  const [recipes, setRecipes] = useState([]);
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [composerSeed, setComposerSeed] = useState({ text: "", nonce: 0 });
  const messagesEndRef = useRef(null);

  const {
    messages,
    isLoading,
    sendUserMessage,
    initializeSession,
    initializeDirectSession,
    currentModule,
    currentSessionId,
    streamingMessageId,
    modules,
    modulesLoading,
    fetchModules,
  } = useChat();

  const activeModuleKey = modules.find((m) => m.name === currentModule)?.key;
  const showRecipePicker =
    Boolean(currentSessionId) && RECIPE_PICKER_MODULE_KEYS.includes(activeModuleKey);

  // Scroll to latest message when chat updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Fetch available modules once
  useEffect(() => {
    fetchModules().catch(() => message.error("Failed to load modules"));
  }, [fetchModules]);

  // Fetch recipes once, lazily, the first time a recipe-related module needs them
  useEffect(() => {
    if (showRecipePicker && recipes.length === 0 && !recipesLoading) {
      setRecipesLoading(true);
      getRecipeList(localStorage.getItem("Token"))
        .then((list) => setRecipes(list))
        .catch(() => message.error("Failed to load recipes"))
        .finally(() => setRecipesLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRecipePicker]);

  const handleSendMessage = (content) => {
    if (content?.trim()) sendUserMessage(content);
  };

  const handleRecipeClick = (recipe) => {
    const label = recipe.product_name
      ? `${recipe.recipe_name} (${recipe.product_name})`
      : recipe.recipe_name;
    const text =
      activeModuleKey === "recipe-summarize"
        ? `Summarize the recipe "${label}".`
        : `I have a question about the recipe "${label}".`;
    sendUserMessage(text);
  };

  const seedComposer = (text) => setComposerSeed({ text, nonce: Date.now() });

  const handleCreateChat = (moduleId, moduleName) => {
    seedComposer("");
    initializeSession(moduleId, moduleName);
  };

  const handleStartSession = () => {
    seedComposer("");
    initializeDirectSession();
  };

  // Used both pre-chat (no session yet — start a general one) and inline,
  // mid-session, before any real message has been sent (just seed the
  // composer in the session that's already open).
  const handleGenericSuggestion = (suggestion) => {
    seedComposer(suggestion.prefill || "");
    if (!currentSessionId) initializeDirectSession();
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ backgroundColor: 'inherit' }}>
      {/* ====== Chat Active ====== */}
      {currentSessionId ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex-1 flex flex-col overflow-hidden"
        >
          {/* Messages List */}
          <div
            className="flex-1 overflow-y-auto px-4 py-4 scrollbar-thin bg-white dark:bg-gray-900"
            id="floating-chat-messages"
          >
            {messages.map((msg) => (
              <FloatingChatMessage
                key={msg.id}
                message={msg}
                isStreaming={streamingMessageId === msg.id}
              />
            ))}

            {/* Recipe Picker — only before the conversation has actually started */}
            {showRecipePicker && messages.length === 1 && (
              <div className="mb-4">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Available Recipes
                </div>
                {recipesLoading ? (
                  <div className="flex justify-center py-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-skin-button-base"></div>
                  </div>
                ) : recipes.length === 0 ? (
                  <div className="text-xs text-gray-500 dark:text-gray-400">No recipes found.</div>
                ) : (
                  <div className="space-y-2 max-h-56 overflow-y-auto scrollbar-thin pr-1">
                    {recipes.map((recipe) => (
                      <motion.button
                        key={recipe.recipe_id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleRecipeClick(recipe)}
                        className="w-full p-2.5 text-left rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 text-sm"
                      >
                        <div className="font-medium text-gray-900 dark:text-white truncate">
                          {recipe.recipe_name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {recipe.product_name ? `${recipe.product_name} · ` : ""}
                          {recipe.recipe_code}
                        </div>
                      </motion.button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Suggestion bubbles — shown inline until the conversation actually
                starts, so landing in a session doesn't strand you at a blank box */}
            {!showRecipePicker && messages.length === 1 && (
              <div className="mb-4">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 text-center">
                  Quick starts
                </div>
                <SuggestionBubbles
                  modules={modules}
                  modulesLoading={modulesLoading}
                  activeModuleName={currentModule}
                  onGenericClick={handleGenericSuggestion}
                  onModuleClick={handleCreateChat}
                />
              </div>
            )}

            {/* Loading Dots */}
            {isLoading && (
              <div className="flex justify-start mb-4">
                <div className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-2xl py-3 px-4 rounded-bl-md">
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      {[0, 150, 300]?.map((delay) => (
                        <div
                          key={delay}
                          className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-400 animate-bounce"
                          style={{ animationDelay: `${delay}ms` }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Field */}
          <FloatingChatInput
            onSendMessage={handleSendMessage}
            disabled={isLoading}
            seedText={composerSeed.text}
            seedNonce={composerSeed.nonce}
          />
        </motion.div>
      ) : (
        /* ====== Welcome Screen ====== */
        <div className="flex-1 flex items-center justify-center p-6 bg-white dark:bg-gray-900 overflow-y-auto">
          <div className="text-center w-full mx-auto">
            {/* Header */}
            <div className="mb-5">
              <div className="w-14 h-14 bg-skin-button-base/10 dark:bg-skin-button-base/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                <MessageCircle className="w-7 h-7 text-skin-button-base" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                AI Assistant
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed px-2">
                Pick a starting point, or just type your own question below.
              </p>
            </div>

            {/* Suggestion bubbles — one assistant, many starting points.
                Clicking any bubble jumps straight into a chat, no separate
                "select a module" step. */}
            <div className="mb-3">
              <SuggestionBubbles
                modules={modules}
                modulesLoading={modulesLoading}
                onGenericClick={handleGenericSuggestion}
                onModuleClick={handleCreateChat}
              />
            </div>

            {/* Plain fallback for anyone who doesn't want a suggestion */}
            <button
              onClick={handleStartSession}
              className="text-xs text-skin-button-base hover:underline"
            >
              Or start a blank chat →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingChatContent;
